import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, gql } from "@apollo/client";

/**
 * R44 (§8) — public Social Share Generator, redesigned as the "Ambassador Microsite" (SPE Phase 2B,
 * presentation-only per SPE_PHASE2_ROADMAP §2B). The email CTA / share links point here. Loads the
 * organiser's config, lets the attendee fill fields + add a photo, composites a banner on a client-side
 * canvas, and shares to an allowed handle (auto-load intent) OR falls back to copy-caption +
 * download-PNG. Every generate/share/click is logged for Reception's attribution + leaderboards.
 *
 * The 2B redesign is PURE presentation over the SAME backend: fixed sections (Hero / Profile / Banner /
 * Caption / Share / Footer) as the future theme substrate, a big live-preview headline, Square/Story size
 * toggle, setup-completion bar, LinkedIn name auto-fill, one-tap caption templates, sticky share bar, and
 * a success screen with confetti + rank. The ONLY new backend touch is the optional read-only
 * `ambassadorStats` query (rank + successful referrals) — it fails soft, so the page works without it.
 */

const GET_CONFIG = gql`
  query ShareGeneratorConfig($eventId: String!, $role: String) {
    shareGeneratorConfig(eventId: $eventId, role: $role) {
      eventId
      role
      enabled
      shareTitle
      shareDescription
      sectionsConfig
      bannerTemplateUrl
      defaultMessage
      includeLink
      layout
      fields
      allowedHandles
      profilePicSource
      bannerWidth
      bannerHeight
      targetRole
      customFonts
      autoPostLinkedin
      ctaConfig
      banners { square { url width height } story { url width height } landscape { url width height } }
      offersBannerChoice
    }
  }
`;
// Optional — prefill the recipient's own name/company/designation. Identify the row by ?prefill=<id>
// (the email CTA) OR ?prefill_email=<email> (the just-registered flow, where only the email is known).
// Fails soft: if the backend doesn't implement it, we simply leave the fields blank for manual entry.
const SHARE_PREFILL = gql`
  query SharePrefill($eventId: String!, $role: String!, $id: String, $email: String) {
    sharePrefill(eventId: $eventId, role: $role, id: $id, email: $email) {
      name
      company
      designation
      stallNo
    }
  }
`;
const ISSUE = gql`
  mutation IssueShareLink($eventId: String!, $role: String, $targetRole: String, $name: String, $email: String, $meta: String, $caption: String, $designation: String, $company: String, $campaignId: Int) {
    issueShareLink(eventId: $eventId, role: $role, targetRole: $targetRole, name: $name, email: $email, meta: $meta, caption: $caption, designation: $designation, company: $company, campaignId: $campaignId)
  }
`;
const SAVE_BANNER = gql`
  mutation SaveShareBanner($token: String!, $pngDataUrl: String!, $width: Int, $height: Int, $name: String, $caption: String, $designation: String, $company: String) {
    saveShareBanner(token: $token, pngDataUrl: $pngDataUrl, width: $width, height: $height, name: $name, caption: $caption, designation: $designation, company: $company)
  }
`;
const LOG = gql`
  mutation LogShareEvent($token: String!, $eventId: String!, $platform: String, $action: String!) {
    logShareEvent(token: $token, eventId: $eventId, platform: $platform, action: $action)
  }
`;
// Optional — the ambassador's live rank + referral progress for the leaderboard teaser. Fails soft:
// if the backend doesn't implement it, `error` is set and we simply hide the rank card.
const AMBASSADOR_STATS = gql`
  query AmbassadorStats($eventId: String!, $role: String, $token: String, $campaignId: Int) {
    ambassadorStats(eventId: $eventId, role: $role, token: $token, campaignId: $campaignId) {
      rank
      successfulReferrals
      referralsToNextRank
      nextMilestone
    }
  }
`;

// api-reg serves the server-rendered /s/:token share-landing page (the URL we actually share, so
// crawlers see the OG banner). Keep it absolute — the shared link must resolve for LinkedIn/FB bots.
const API_BASE = "https://api-reg.eventegration.com";

// Which platforms actually honor a prefilled caption `text` param (research-confirmed):
//  - twitter / whatsapp / telegram → YES; facebook `quote` is unreliable; LinkedIn IGNORES text
//    (its card is built purely from the target page's OG tags — which is why /s/:token carries them).
const AUTO_LOAD = {
  // NOTE: `quote` was removed by Facebook in 2020 (ignored) — so we don't send it. On DESKTOP the sharer
  // dialog still works and reads the caption from the page's OG card. On MOBILE the FB app intercepts this
  // URL and opens the app WITHOUT a composer (a platform limitation, not our bug) — so onShare() routes
  // mobile Facebook through the OS share sheet (fallback: the guided manual-post modal).
  facebook: (u) => `https://www.facebook.com/sharer/sharer.php?u=${u}`,
  twitter: (u, t) => `https://twitter.com/intent/tweet?url=${u}&text=${t}`,
  linkedin: (u) => `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
  whatsapp: (u, t) => `https://wa.me/?text=${t}%20${u}`,
  telegram: (u, t) => `https://t.me/share/url?url=${u}&text=${t}`,
  email: (u, t) => `mailto:?subject=${t}&body=${t}%0A%0A${u}`,
};
// Platforms where the caption text genuinely prefills the composer.
const CAPTION_PREFILL = new Set(["twitter", "whatsapp", "telegram", "email"]);
// A "just open the app/site" URL for the manual-post modal's final step (no prefill, just launch it).
// For LinkedIn/Facebook we deep-link straight to the NEW-POST composer so the user lands on a post box
// (not the feed) ready to attach the downloaded card + paste the caption.
const APP_OPEN_URL = {
  facebook: "https://www.facebook.com/sharer/sharer.php",
  instagram: "https://www.instagram.com/",
  linkedin: "https://www.linkedin.com/feed/?shareActive=true",
};

// Mobile-UA check for the Facebook path ONLY: on phones the FB app hijacks sharer.php without a
// composer, so mobile Facebook goes through the OS share sheet instead (see onShare). Modern iPads
// report a Mac UA and correctly take the desktop sharer-popup path.
function isMobileUA() {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
}

// Brand + platform swatches for the share buttons.
const PLATFORM_META = {
  linkedin: { label: "LinkedIn", bg: "#0A66C2", glyph: "in" },
  whatsapp: { label: "WhatsApp", bg: "#25D366", glyph: "✆" },
  twitter: { label: "X", bg: "#111111", glyph: "𝕏" },
  facebook: { label: "Facebook", bg: "#1877F2", glyph: "f" },
  telegram: { label: "Telegram", bg: "#229ED9", glyph: "✈" },
  email: { label: "Email", bg: "#6D28D9", glyph: "✉" },
  instagram: { label: "Instagram", bg: "#E4405F", glyph: "◉" },
};

const CANVAS = 1080;

export default function ShareGeneratorPage() {
  // PER-ROLE (owner 2026-07-13): /share/:eventId (event-wide) OR /share/:eventId/:role.
  const { eventId, role } = useParams();
  // SPE: the $shareGenerator email CTA appends &campaign=<id> (server-derived + canonical). We forward it
  // to issueShareLink, which RE-VALIDATES it against the event before stamping — so even a tampered value
  // can't attribute across events. Absent → the share is "Uncategorised" (the back-compat path).
  const campaignId = (() => {
    const raw = new URLSearchParams(window.location.search).get("campaign");
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  // The email CTA carries ?prefill=<recipient id>; the post-registration CTA carries ?prefill_email=<email>.
  // Either identifies the recipient's own row so we can auto-fill name/company/designation.
  const prefillId = (() => {
    const v = new URLSearchParams(window.location.search).get("prefill");
    return v && v.trim() ? v.trim() : null;
  })();
  const prefillEmail = (() => {
    const v = new URLSearchParams(window.location.search).get("prefill_email");
    return v && v.trim() ? v.trim() : null;
  })();
  const { data, loading } = useQuery(GET_CONFIG, { variables: { eventId, role: role || null } });
  const [issueShareLink] = useMutation(ISSUE);
  const [saveShareBanner] = useMutation(SAVE_BANNER);
  const [logShareEvent] = useMutation(LOG);

  const cfg = data && data.shareGeneratorConfig;

  /**
   * Is this page section switched on?
   *
   * Organisers want different amounts of explanation here — one asked for the subtitle and the
   * desktop hint gone, another wants both — so each piece is switchable per event and per role
   * from Reception.
   *
   * Defaults to SHOWN whenever the answer isn't a definite false: no config (every event that
   * predates this), unparseable JSON, or a key that simply isn't there. That last case matters —
   * it means adding a section later cannot silently blank it on events configured before it
   * existed.
   */
  const sections = (() => {
    try {
      const raw = cfg && cfg.sectionsConfig;
      if (!raw) return {};
      return typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch (_) {
      return {};
    }
  })();
  const show = (key) => sections[key] !== false;
  const [values, setValues] = useState({});
  const [photoUrl, setPhotoUrl] = useState(null);
  const [token, setToken] = useState(null);

  // Load the organiser's custom uploaded fonts (@font-face) so the banner canvas can draw them. Each is
  // { family, url }; `document.fonts.ready` in draw() then waits for them. Fails soft (bad url → skip).
  useEffect(() => {
    let fonts = [];
    try {
      fonts = cfg && cfg.customFonts ? JSON.parse(cfg.customFonts) : [];
    } catch (_) {
      fonts = [];
    }
    if (!Array.isArray(fonts) || !fonts.length || typeof FontFace === "undefined") return;
    fonts.forEach((f) => {
      if (!f || !f.family || !f.url) return;
      try {
        const face = new FontFace(f.family, `url(${f.url})`);
        face.load().then((loaded) => document.fonts && document.fonts.add(loaded)).catch(() => {});
      } catch (_) {
        /* skip a bad font, keep the rest */
      }
    });
  }, [cfg]);
  const [pngUrl, setPngUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [caption, setCaption] = useState("");        // editable, seeded from defaultMessage
  const [captionTouched, setCaptionTouched] = useState(false);
  // Preserve the user's OWN typed message when they tap a template chip, so switching chips never
  // silently discards what they wrote — a "Yours" chip restores it. Seeded/updated whenever the current
  // caption isn't one of the templates (i.e. it's the user's custom text).
  const [customDraft, setCustomDraft] = useState("");
  const [sharing, setSharing] = useState(false);      // disable buttons while persisting the banner
  const [exportErr, setExportErr] = useState("");     // tainted-canvas / export failure surface
  const [cameraOpen, setCameraOpen] = useState(false);// in-app camera capture modal
  const [toast, setToast] = useState(null);           // transient bottom toast {msg,n} (share-copy guidance)
  // Presentation-only UI state (2B):
  const [size, setSize] = useState("square");         // 'square' | 'story' | 'landscape' — preview aspect only
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [liName, setLiName] = useState(false);        // highlight the name field after LinkedIn import
  const [shared, setShared] = useState(null);         // null = builder view; platform label = success view
  const [manual, setManual] = useState(null);         // {platform, downloaded, copied} — manual-post step modal
  // AP-1 (UX_SPEC_LINKEDIN_AUTOPOST.md): 1-tap LinkedIn auto-post modal state.
  const [autoPost, setAutoPost] = useState(null);     // null | {step:'preview'|'publishing'|'done', postUrl}
  const [autoPosted, setAutoPosted] = useState(false);// success flag → button relabels "Posted ✓ — post again"
  const [autoPostOff, setAutoPostOff] = useState(false); // server said 403 (config raced) → hide the button
  const [pendingAutoPost, setPendingAutoPost] = useState(false); // reopen preview after the OAuth round-trip
  const canvasRef = useRef(null);
  // Monotonic id of the newest draw() run — see the race guard inside draw().
  const drawRunRef = useRef(0);
  const uploadInputRef = useRef(null);                 // the hidden Upload file input (camera-modal fallback)

  // Transient toast (auto-dismiss). A nonce forces the timer to restart even when the SAME message is
  // shown twice in a row (setState with an identical string is a no-op and wouldn't reset the timer).
  const showToast = useCallback((msg) => setToast({ msg, n: (typeof performance !== "undefined" ? performance.now() : 0) }), []);
  useEffect(() => {
    if (!toast || !toast.msg) return;
    const t = setTimeout(() => setToast(null), 4500);
    return () => clearTimeout(t);
  }, [toast]);

  // Optional leaderboard teaser — fails soft (query may not exist on older backends).
  const { data: statsData } = useQuery(AMBASSADOR_STATS, {
    variables: { eventId, role: role || null, token, campaignId },
    skip: !eventId,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });
  const stats = statsData && statsData.ambassadorStats;

  // Prefill the recipient's own details from the email CTA (?prefill=<id>). Fails soft (errorPolicy:all →
  // if the backend query is absent, `error` is set and we just skip). Seeds `values` ONCE, and never
  // overwrites anything the user has already typed / a LinkedIn import already set (existing value wins).
  const { data: prefillData } = useQuery(SHARE_PREFILL, {
    variables: { eventId, role: role || "", id: prefillId || null, email: prefillEmail || null },
    skip: !eventId || !role || (!prefillId && !prefillEmail),
    fetchPolicy: "cache-first",
    errorPolicy: "all",
  });
  useEffect(() => {
    const p = prefillData && prefillData.sharePrefill;
    if (!p) return;
    setValues((v) => {
      const next = { ...v }; // keep everything already set; prefill only fills BLANK fields
      const fill = (k, val) => { if (val && !(next[k] && String(next[k]).trim())) next[k] = val; };
      fill("name", p.name);
      fill("company", p.company);
      fill("designation", p.designation);
      fill("stallNo", p.stallNo);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillData]);

  // Seed the editable caption from the organiser's default once the config loads — with $tokens/{tokens}
  // ALREADY resolved (owner 2026-08-07: "$stallNo raw dikha; message box me hi resolve hona chahiye").
  // Until the user edits the caption (captionTouched), we RE-seed from the RAW default whenever their
  // values change, so a stall no. that arrives via prefill a moment later still lands in the visible
  // text (seeding from the raw source each time means an earlier empty-value pass can't eat the token).
  // Once touched, their text is theirs — egress interpolation still resolves anything they type.
  useEffect(() => {
    if (cfg && !captionTouched) {
      const raw = cfg.defaultMessage || cfg.shareTitle || "I'm attending — join me!";
      setCaption(
        interpolate(raw, {
          stallNo: values.stallNo || "",
          company: values.company || "",
          designation: values.designation || "",
          name: values.name || "",
          eventName: cfg.shareTitle || "the event",
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg, captionTouched, values.stallNo, values.company, values.designation, values.name]);

  // Returning from Sign-in-with-LinkedIn (api-reg bounces back with ?li_photo=<url>&li=1). Adopt the
  // fetched LinkedIn photo + restore the token so the same share record is used.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const li = q.get("li");
    if (li !== "1" && li !== "error") return;
    // Restore the draft inputs saved before the LinkedIn redirect (name/caption/etc.).
    let photoDropped = false;
    try {
      const raw = sessionStorage.getItem("eg_share_draft");
      if (raw) {
        const d = JSON.parse(raw);
        if (d.values) setValues(d.values);
        if (typeof d.caption === "string") { setCaption(d.caption); setCaptionTouched(!!d.captionTouched); }
        if (d.token) setToken(d.token);
        // Restore the photo the user had BEFORE the redirect. If they chose "Use LinkedIn photo" the
        // block below overwrites this with the LinkedIn one — that request is newer, so it wins.
        if (typeof d.photoUrl === "string" && d.photoUrl) setPhotoUrl(d.photoUrl);
        photoDropped = !!d.photoDropped;
        sessionStorage.removeItem("eg_share_draft");
      }
    } catch { /* ignore */ }
    const liPhoto = q.get("li_photo");
    const liNameParam = q.get("li_name");
    const liToken = q.get("token");
    if (li === "1" && liPhoto && liPhoto !== "0") {
      setPhotoUrl(decodeURIComponent(liPhoto));
      if (liToken) setToken(liToken);
      // 2C: if LinkedIn also returned a name, auto-fill it (editable) and briefly highlight the field.
      if (liNameParam && liNameParam !== "0") {
        const nm = decodeURIComponent(liNameParam);
        setValues((v) => (v.name ? v : { ...v, name: nm }));
        setLiName(true);
        setTimeout(() => setLiName(false), 4000);
      }
    } else if (li === "error") {
      setExportErr("Couldn't get your LinkedIn photo. You can upload one or use your initials.");
    }
    // Their photo was too large to survive the redirect. Say so and STOP the auto-resume: posting a
    // banner with a placeholder where their face should be is exactly the silent failure being fixed.
    const usedLinkedInPhoto = li === "1" && liPhoto && liPhoto !== "0";
    if (photoDropped && !usedLinkedInPhoto) {
      setExportErr("Your photo was too large to keep through the LinkedIn sign-in — please add it again before posting.");
    }
    // AP-1: returning from a connect-for-POSTING round-trip (?li_intent=post) → re-open the preview
    // modal once the restored draft has re-drawn the banner (the effect below waits for pngUrl+token).
    if (li === "1" && q.get("li_intent") === "post" && !(photoDropped && !usedLinkedInPhoto)) setPendingAutoPost(true);
    // Clean the query so a refresh doesn't re-trigger.
    window.history.replaceState({}, "", window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // AP-1: after the OAuth round-trip, FINISH the publish (owner 2026-08-07: silently reopening the
  // preview read as "share hua hi nahi, pata bhi nahi chala"). The user pressed Publish BEFORE the
  // redirect — that tap was the explicit consent for THIS exact post (draft-restored, unchanged) —
  // so the modal reopens straight in 'publishing' and completes. Waits for the re-rendered banner
  // (pngUrl) + the restored token so nothing publishes half-prepped.
  useEffect(() => {
    if (pendingAutoPost && pngUrl && token) {
      setPendingAutoPost(false);
      resumeAutoPostAfterConnect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAutoPost, pngUrl, token]);

  // safeParse only falls back when the JSON is UNPARSEABLE — valid JSON of the wrong TYPE still gets
  // through. A `layout` column holding the text "null" parses to null and `layout.photo` then throws a
  // TypeError that blanks the entire page; a `fields` of "{}" breaks `fields.some(...)` the same way.
  // The server passes any string column through verbatim (resolvers/shareGenerator.js:207), so this is
  // reachable from data alone. Enforce the SHAPE here — one bad column must never white-screen a
  // sharer mid-event.
  const layout = asObject(safeParse(cfg && cfg.layout, {}), {});
  const fields = asArray(safeParse(cfg && cfg.fields, []), []);
  const allowed = asArray(safeParse(cfg && cfg.allowedHandles, []), []);
  /*
   * A2 — the chosen variant drives BOTH the source image and the canvas.
   * A chooser appears only when both variants exist; with one, `size` is forced to that one, so a
   * stale selection can never point at a variant the organiser has not uploaded.
   */
  const bannerSet = bannerVariants(cfg);
  const haveVariants = variantsOf(bannerSet);
  // More than one, not exactly two: with landscape there are three possible variants, and two of
  // them must still offer a choice.
  const offerChoice = haveVariants.length > 1;
  const activeVariant = offerChoice
    ? (bannerSet[size] ? size : haveVariants[0])
    : (haveVariants[0] || null);
  const activeBanner = activeVariant ? bannerSet[activeVariant] : null;
  // The FILE's real pixels are the truth; the preset is only a fallback when they are unknown.
  const cw = (activeBanner && activeBanner.w) || (cfg && Number(cfg.bannerWidth)) || CANVAS;
  const ch = (activeBanner && activeBanner.h) || (cfg && Number(cfg.bannerHeight)) || CANVAS;
  // Live token values for caption interpolation, resolved at share-time via interpolate() at the egress
  // points below. Hoisted here (above ensureToken) so every share/copy path sees it. {eventName} = the
  // organiser's share title; the rest come from the sharer's (prefilled + editable) values.
  const captionTokens = {
    stallNo: values.stallNo || "",
    company: values.company || "",
    designation: values.designation || "",
    name: values.name || "",
    eventName: (cfg && cfg.shareTitle) || "the event",
  };

  // Draw the banner whenever inputs change. (Canvas is always the organiser's configured cw×ch — the
  // Square/Story toggle only changes the on-screen PREVIEW box, never the exported artwork dimensions.)
  const draw = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // RACE GUARD. This effect is async and awaits fonts + the template + the photo, so a run started
    // BEFORE the user set their photo can still be suspended when a newer run finishes. Both runs share
    // one canvas and both call setPngUrl, so without this the STALE run repaints last and overwrites the
    // export — the photo appears in the preview, then vanishes from the posted banner. Only the newest
    // run may draw or publish a result.
    const myRun = ++drawRunRef.current;
    const isStale = () => myRun !== drawRunRef.current;
    const ctx = canvas.getContext("2d");
    // Fonts must be LOADED before canvas draws them, else it silently falls back to the default face.
    // Wait for all declared fonts (the Google <link> families + any custom @font-face) to be ready.
    try {
      if (document.fonts && document.fonts.ready) await document.fonts.ready;
    } catch (_) {
      /* non-fatal — draw with whatever is loaded */
    }
    if (isStale()) return;              // a newer run owns the canvas now
    ctx.clearRect(0, 0, cw, ch);
    const src = (activeBanner && activeBanner.url) || (cfg && cfg.bannerTemplateUrl);
    if (src) {
      try {
        const img = await loadImg(src);
        if (isStale()) return;          // template arrived late — do not repaint over a newer run
        drawCover(ctx, img, cw, ch);
      } catch {
        bg(ctx, cw, ch);
      }
    } else bg(ctx, cw, ch);

    // Profile pic frame — draws the photo if provided, else a placeholder so the frame is visible.
    if (layout.photo) {
      const w = layout.photo.size || 220;
      const x = layout.photo.x;
      const y = layout.photo.y;
      ctx.save();
      clipPhoto(ctx, x, y, w, layout.photo.shape || "circle", layout.photo.radius || 40);
      if (photoUrl) {
        try {
          const img = await loadImg(photoUrl);
          if (isStale()) { ctx.restore(); return; }
          ctx.drawImage(img, x, y, w, w);
        } catch {
          initialsAvatar(ctx, x, y, w, values.name);
        }
      } else {
        // No uploaded photo → a deterministic initials avatar (always-works fallback) so the banner
        // never ships with an empty grey frame. Falls back to a generic silhouette if no name yet.
        if ((values.name || "").trim()) initialsAvatar(ctx, x, y, w, values.name);
        else photoPlaceholder(ctx, x, y, w);
      }
      ctx.restore();
    }
    // Only draw a text element when the organiser SHOWS that field (Reception "Show Name/Designation/
    // Company" toggles = the `fields` list). A field turned OFF must not appear on the banner even though
    // its layout box still has coordinates. (Owner 2026-07-27: Name was hidden in config but still drew.)
    const showKey = (k) => fields.some((f) => f.key === k);
    // Collision-avoidance (owner 2026-08-05): the text stack name → designation → company → stallNo is
    // drawn top-to-bottom. If a field wraps to multiple lines it would overlap the next one, so we track a
    // running "push" offset: whenever a field's drawn bottom exceeds the next field's configured top, the
    // next field (and everything below it) is nudged down by the overflow. Fields keep their own x/align.
    const stack = ['name', 'designation', 'company', 'stallNo'].filter((k) => showKey(k) && layout[k]);
    let push = 0; // cumulative downward nudge from earlier fields wrapping
    for (let i = 0; i < stack.length; i++) {
      const k = stack[i];
      const box = layout[k];
      const drawnY = box.y + push;
      const height = text(ctx, values[k], { ...box, y: drawnY });
      // Where this field's content ends (y is the first line's center; height covers all lines).
      const bottom = drawnY - (box.size || 44) / 2 + height;
      const next = stack[i + 1] && layout[stack[i + 1]];
      if (next) {
        const nextTop = next.y + push - (next.size || 44) / 2;
        const gap = 6; // small breathing space between fields
        if (bottom + gap > nextTop) push += bottom + gap - nextTop; // nudge the rest down
      }
    }
    // Any other showable text field not in the ordered stack → draw at its own box (no stacking).
    for (const f of fields) {
      const k = f.key;
      if (['name', 'designation', 'company', 'stallNo'].includes(k)) continue;
      if (layout[k] && values[k]) text(ctx, values[k], layout[k]);
    }
    if (isStale()) return;              // never let a stale run overwrite a newer export
    try {
      setPngUrl(canvas.toDataURL("image/png"));
    } catch {
      setPngUrl(null); // tainted canvas (template without CORS) — preview still shows
    }
  // A2: activeBanner MUST be a dependency — it is the chosen variant's source image. Without it,
  // switching Square/Story changed the canvas size but never repainted the artwork, which is the
  // same class of bug (a control that appears to work and does not) that A2 exists to remove.
  }, [cfg, layout, values, photoUrl, cw, ch, activeBanner]);

  useEffect(() => {
    draw();
  }, [draw]);

  // Mint the sharer token once, carrying the identity + caption that the /s/:token OG card composes.
  async function ensureToken() {
    if (token) return token;
    try {
      const res = await issueShareLink({
        variables: {
          eventId,
          role: role || null,
          // Cross-role share target — forward the config's targetRole onto the token so the /s/:token
          // "Register Now" opens that role's registration (else the sharer's own role). Card/attribution
          // stay under `role`. The page only knows its own :role; the server does the URL resolution.
          targetRole: (cfg && cfg.targetRole) || null,
          name: values.name || null,
          email: values.email || null,
          meta: JSON.stringify(values),
          caption: interpolate(caption, captionTokens) || null,
          designation: values.designation || null,
          company: values.company || null,
          campaignId,
        },
      });
      const t = res.data.issueShareLink;
      setToken(t);
      return t;
    } catch {
      return null;
    }
  }

  // Persist the composed banner PNG + the LIVE name/caption on the server, so /s/:token serves a real
  // og:image AND its title/description match the banner (the token was minted once, values may have
  // changed since). Returns false + surfaces an error if the canvas can't be exported (tainted).
  async function persistBanner(t) {
    if (!t) return false;
    let png = pngUrl;
    if (!png) {
      try {
        png = canvasRef.current && canvasRef.current.toDataURL("image/png");
      } catch {
        png = null;
      }
    }
    if (!png) {
      setExportErr("The banner image couldn't be exported (the template image blocks cross-origin use). Ask the organiser to host the template with CORS enabled.");
      return false;
    }
    // The server rejects a banner over 5 MB (saveShareBanner) and api-reg caps the request body at 6 MB.
    // A large organiser canvas (e.g. 2000×2000 photographic) exceeds that as a PNG, and EVERY retry then
    // fails identically — "please try again" would be advice that cannot work. Re-encode as JPEG (much
    // smaller for photos) and send that instead; the server already accepts image/jpeg.
    const TOO_BIG = 4_800_000; // a little under the 5 MB server cap, leaving room for the rest of the body
    if (png.length > TOO_BIG) {
      try {
        const jpeg = canvasRef.current && canvasRef.current.toDataURL("image/jpeg", 0.9);
        if (jpeg && jpeg.length <= TOO_BIG) png = jpeg;
      } catch { /* keep the PNG and let the server decide */ }
    }
    try {
      await saveShareBanner({
        variables: {
          token: t, pngDataUrl: png, width: cw, height: ch,
          name: values.name || null, caption: interpolate(caption, captionTokens) || null,
          designation: values.designation || null, company: values.company || null,
        },
      });
      return true;
    } catch (e) {
      // Distinguish "too large" (retrying is useless — the organiser must shrink the banner) from a
      // transient failure, instead of telling everyone to try again.
      const msg = String((e && e.message) || "");
      if (png.length > TOO_BIG || /exceeds|too large|payload|entity/i.test(msg)) {
        setExportErr("This event's banner is too large to upload. Please tell the organiser to reduce the banner size.");
      } else {
        setExportErr("Couldn't save the banner. Please check your connection and try again.");
      }
      return false;
    }
  }

  // The URL we SHARE = the server-rendered landing page (has the OG banner + Register CTA).
  function landingUrl(t, platform) {
    return `${API_BASE}/s/${t}${platform ? `?platform=${encodeURIComponent(platform)}` : ""}`;
  }

  async function onShare(platform) {
    if (sharing) return;
    // Manual-post platforms (no web share-intent, e.g. Instagram) can't be opened with a prefilled
    // composer — the user must download the card + copy the caption and post it themselves. Instead of
    // doing that SILENTLY, open a guided "how to post" modal. Attribution is PRESERVED: the copy/download
    // inside the modal bake THIS platform's own string into the URL (?platform=<platform>), so a manual
    // Instagram share still attributes to `sharing_platform=instagram`, not a lossy "copy" bucket.
    if (!AUTO_LOAD[platform]) {
      openManualModal(platform);
      return;
    }
    // LinkedIn: a URL-share ignores the caption and can't attach an image → guided image-post modal
    // (download → new post → attach → paste). Owner 2026-07-28. 1-tap posting is designed separately
    // (PLAN_SOCIAL_AUTOPOST.md) and does NOT change this path until it ships.
    if (platform === "linkedin") {
      openManualModal(platform);
      return;
    }
    // Facebook: owner ruling 2026-08-06 (InGo-parity) — 1-click share dialog BEATS the big-image manual
    // flow. DESKTOP falls through to the sharer.php popup below (FB builds the card from /s/:token's OG
    // tags; the caption lands in the clipboard via the !prefills toast). MOBILE: the FB app intercepts
    // sharer.php WITHOUT a composer, so use the OS share sheet; no share sheet / it refuses → the old
    // guided manual modal, so the tap never dead-ends. Sheet CANCELLED by the user → stay quietly.
    if (platform === "facebook" && isMobileUA()) {
      if (!navigator.share) {
        openManualModal(platform);
        return;
      }
      setSharing(true);
      setExportErr("");
      try {
        const t = await ensureToken();
        if (!t) { setExportErr("Couldn't start the share — please try again."); return; }
        const ok = await persistBanner(t);
        if (!ok) return;
        logShareEvent({ variables: { token: t, eventId, platform, action: "shared" } }).catch(() => {});
        try {
          await navigator.share({ text: interpolate(caption, captionTokens), url: landingUrl(t, platform) });
          setShared(PLATFORM_META.facebook.label);
        } catch (e) {
          if (!e || e.name !== "AbortError") openManualModal(platform);
        }
      } finally {
        setSharing(false);
      }
      return;
    }
    setSharing(true);
    setExportErr("");
    // Open the popup SYNCHRONOUSLY (browsers block window.open after an await) — then point it once
    // ready. NOTE: no `noopener` in the features string — that would make window.open return null and
    // break the sync-popup strategy. Cross-origin isolation isn't needed for a share intent.
    const intent = AUTO_LOAD[platform];
    // mailto: is handled by the OS, not a popup — don't open a blank window for it.
    const usePopup = platform !== "email";
    const win = usePopup ? window.open("about:blank", "_blank", "width=600,height=640") : null;
    const label = (PLATFORM_META[platform] && PLATFORM_META[platform].label) || cap(platform);
    try {
      const t = await ensureToken();
      // Token mint failed — surface it (don't just flash a popup and vanish, which reads as "broken").
      if (!t) { if (win) win.close(); setExportErr("Couldn't start the share — please try again."); return; }
      // BLOCK the share if the banner couldn't be persisted — otherwise the landing page has no
      // og:image (the exact bug this feature fixes). persistBanner already surfaces the error.
      const ok = await persistBanner(t);
      if (!ok) { if (win) win.close(); return; }
      logShareEvent({ variables: { token: t, eventId, platform, action: "shared" } }).catch(() => {});
      const url = encodeURIComponent(landingUrl(t, platform));
      // Only send caption text to platforms that honor it; LinkedIn/FB read it from the OG card.
      const prefills = CAPTION_PREFILL.has(platform);
      const text = encodeURIComponent(prefills ? interpolate(caption, captionTokens) : "");
      // Platforms that DON'T auto-fill the caption (LinkedIn, Facebook on desktop): try to copy the message
      // so the user can paste it. Only claim "copied" if the clipboard write actually succeeded; otherwise
      // still guide them (they can use the "Copy message" button below). Guide even with an empty caption.
      if (!prefills) {
        let copied = false;
        const captionOut = interpolate(caption, captionTokens);
        if (captionOut) {
          try { await navigator.clipboard.writeText(captionOut); copied = true; } catch { copied = false; }
        }
        showToast(
          copied
            ? `${label} doesn't allow auto-filling the text. Your message is copied — just paste it into the post.`
            : `${label} doesn't fill the text automatically. Use "Copy message" below, then paste it into your post.`,
        );
      }
      if (platform === "email") {
        // mailto: opens the OS mail client; if none is configured nothing happens, so DON'T claim success.
        window.location.href = intent(url, text);
        // Best-effort: we can't reliably detect a missing mail handler, so show a neutral confirmation
        // rather than the celebratory "Posted!" screen for email.
        showToast("Opening your email app… if nothing opens, use \"Copy message\" and \"Download card\" below.");
        return;
      }
      const target = intent(url, text);
      let opened = win;
      if (win) win.location.href = target;
      else opened = window.open(target, "_blank", "width=600,height=640");
      // If the popup was blocked (opened === null), do NOT show a false success screen — tell the user and
      // point them at the copy/download fallback, so it never looks like the app "did nothing" or lied.
      if (!opened) {
        setExportErr(`Your browser blocked the ${label} window. Please allow pop-ups for this site, or use "Copy message" + "Download card" below to share manually.`);
        return;
      }
      // Success screen — only after a real share window actually opened.
      setShared(label);
    } finally {
      setSharing(false);
    }
  }

  // Open the guided manual-post modal for a no-intent platform (Instagram) OR the generic "copy anywhere"
  // flow. `platform` carries the attribution string that copy/download inside the modal will use.
  async function openManualModal(platform) {
    setExportErr("");
    setManual({ platform, downloaded: false, copied: false, exportErr: "" });
    // Mint the token + persist the banner up front so both the download and the copy inside the modal
    // point at a live /s/:token with a real og:image (and log a 'shared' event for the funnel).
    const t = await ensureToken();
    if (!t) { setManual((m) => m && { ...m, exportErr: "Couldn't prepare your card — please try again." }); return; }
    const ok = await persistBanner(t);
    // If the banner couldn't be exported (tainted canvas / no CORS on the template), tell the user INSIDE
    // the modal — otherwise they'd tap "Download card" and nothing happens with the error hidden behind it.
    if (!ok) setManual((m) => m && { ...m, exportErr: "The card image couldn't be prepared (the event's template blocks cross-origin use). You can still copy your message and link below." });
    logShareEvent({ variables: { token: t, eventId, platform, action: "shared" } }).catch(() => {});
  }

  // Copy the caption + tracked link. `platform` sets the attribution bucket (?platform=<platform>) — the
  // callers pass a REAL platform (instagram/twitter/…) so per-platform attribution is preserved; only the
  // explicit "Copy link anywhere" button uses the generic "copy" bucket. Fires a 'shared' log too (the old
  // copy path skipped it, so copied shares never showed in the funnel).
  async function copyForPlatform(platform) {
    if (sharing) return;
    setSharing(true);
    try {
      const t = await ensureToken();
      if (!t) return;
      await persistBanner(t);
      logShareEvent({ variables: { token: t, eventId, platform, action: "shared" } }).catch(() => {});
      await writeCaptionToClipboard(t, platform);
    } finally {
      setSharing(false);
    }
  }
  async function writeCaptionToClipboard(t, platform) {
    const full = `${interpolate(caption, captionTokens)} ${landingUrl(t, platform || "copy")}`;
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (HTTP / in-app webview / permission). Don't fail silently — tell the user so
      // "Copy message" never looks broken; they can select the caption in the box and copy it manually.
      showToast("Couldn't copy automatically — please select your message in the box above and copy it.");
    }
  }
  function downloadPng() {
    let png = pngUrl;
    if (!png) {
      try { png = canvasRef.current && canvasRef.current.toDataURL("image/png"); } catch { png = null; }
    }
    if (!png) {
      setExportErr("Couldn't export the banner (the template image may block cross-origin use).");
      return;
    }
    const a = document.createElement("a");
    a.href = png;
    a.download = `share-${eventId}.png`;
    a.click();
  }

  // Stash everything the user has entered before a full-page OAuth redirect, so the return trip can
  // restore it. ONE helper for BOTH redirect sites (photo-connect and post-connect) — they drifted
  // apart before, and the copy that forgot `photoUrl` silently dropped the user's uploaded picture:
  // the banner re-drew with an initials placeholder and posted WITHOUT their photo (owner 2026-08-15).
  //
  // `photoUrl` is a base64 data URL for an upload/camera shot, so it can be megabytes while
  // sessionStorage caps around 5MB. Oversized photos are therefore dropped DELIBERATELY (draft still
  // saved, just without the picture) rather than letting the setItem throw and lose the whole draft —
  // and the return path tells the user their photo needs re-adding instead of silently posting without it.
  function saveDraft(t) {
    const base = { values, caption, captionTouched, token: t };
    try {
      const p = typeof photoUrl === "string" ? photoUrl : null;
      // ~3.5MB of base64 — comfortably under the ~5MB quota with room for the rest of the draft.
      const fits = p && p.length <= 3_500_000;
      sessionStorage.setItem(
        "eg_share_draft",
        JSON.stringify({ ...base, photoUrl: fits ? p : null, photoDropped: !!p && !fits })
      );
    } catch {
      // Quota or serialisation failure → keep the TEXT draft at minimum, never lose their typing.
      try {
        sessionStorage.setItem("eg_share_draft", JSON.stringify({ ...base, photoUrl: null, photoDropped: !!photoUrl }));
      } catch { /* ignore */ }
    }
  }

  // "Use LinkedIn photo" → start the Sign-in-with-LinkedIn flow (api-reg handles OIDC). This is a
  // full-page redirect, so stash the user's inputs (name/caption/etc.) in sessionStorage first and
  // restore them on return — otherwise React state resets and they'd have to re-type everything.
  async function onUseLinkedInPhoto() {
    const t = await ensureToken();
    if (!t) return;
    saveDraft(t);
    const ret = encodeURIComponent(window.location.href.split("?")[0]);
    window.location.href = `${API_BASE}/auth/linkedin?token=${encodeURIComponent(t)}&return=${ret}`;
  }

  // AP-1 — open the auto-post preview (the consent moment). Token + banner are prepped FIRST so the
  // modal always previews the REAL card that would be posted; prep failure surfaces via the existing
  // exportErr and the modal never opens blank (UX_SPEC_LINKEDIN_AUTOPOST.md A2/A3).
  async function openAutoPostPreview() {
    if (sharing) return;
    setSharing(true);
    setExportErr("");
    try {
      const t = await ensureToken();
      if (!t) { setExportErr("Couldn't start the post — please try again."); return; }
      const ok = await persistBanner(t);
      if (!ok) return; // persistBanner already surfaced why
      setAutoPost({ step: "preview" });
    } finally {
      setSharing(false);
    }
  }

  // AP-1 — back from the connect round-trip: prep token+banner, then publish VISIBLY (modal opens in
  // 'publishing'). A repeat not-connected answer falls back to the preview — never a redirect loop.
  async function resumeAutoPostAfterConnect() {
    if (sharing) return;
    setSharing(true);
    setExportErr("");
    try {
      const t = await ensureToken();
      if (!t) { setExportErr("Couldn't finish your LinkedIn post — please try again."); return; }
      const ok = await persistBanner(t);
      if (!ok) return;
      await publishAutoPost({ resumed: true });
    } finally {
      setSharing(false);
    }
  }

  // AP-1 — Publish tapped: api-reg posts server-side with the member's own LinkedIn permission, then
  // discards it. NO frontend 'shared' log here — the backend writes the 'autoposted' funnel row.
  async function publishAutoPost(opts) {
    const resumed = !!(opts && opts.resumed === true);
    const t = token;
    if (!t) { setAutoPost(null); return; }
    setAutoPost({ step: "publishing" });
    let res = null;
    let body = {};
    try {
      res = await fetch(`${API_BASE}/auth/linkedin/post`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: t }),
      });
      body = await res.json().catch(() => ({}));
    } catch { /* network failure → generic fail path below */ }
    if (res && res.ok && body && body.ok) {
      setAutoPosted(true);
      setAutoPost({ step: "done", postUrl: body.postUrl || null });
      return;
    }
    const status = res ? res.status : 0;
    // Not connected yet (400) / connection expired (401) → the normal one-tap re-auth, NOT an error
    // (spec A2). Save the draft exactly like onUseLinkedInPhoto so nothing the user typed is lost.
    if (status === 400 || status === 401) {
      // Post-connect resume: the server SHOULD have the token now — if it still says not-connected,
      // do NOT redirect again (no loops). Land on the preview for one explicit tap instead.
      if (resumed) {
        setAutoPost({ step: "preview" });
        showToast("You're connected — press Publish to post.");
        return;
      }
      if (status === 401) showToast("Your LinkedIn connection expired — please connect again.");
      saveDraft(t);
      const ret = encodeURIComponent(`${window.location.href.split("?")[0]}?li_intent=post`);
      window.location.href = `${API_BASE}/auth/linkedin?token=${encodeURIComponent(t)}&return=${ret}`;
      return;
    }
    // Organiser switched the toggle off after the page loaded (403) — hide the button, no error screen.
    if (status === 403) {
      setAutoPost(null);
      setAutoPostOff(true);
      return;
    }
    // Real failure → the EXISTING guided manual flow with the reason inside it — never a dead-end.
    setAutoPost(null);
    setManual({
      platform: "linkedin",
      downloaded: false,
      copied: false,
      exportErr: body && body.message
        ? `Couldn't post automatically — ${body.message}`
        : "Couldn't post automatically — here's the 30-second manual way.",
    });
  }

  if (loading) return <Center>Loading…</Center>;
  if (!cfg || !cfg.enabled)
    return <Center>Sharing isn’t enabled for this event yet.</Center>;

  const showUpload = cfg.profilePicSource === "upload" || cfg.profilePicSource === "both";
  const showLinkedInPic = cfg.profilePicSource === "linkedin" || cfg.profilePicSource === "both";

  // ---- derive completion steps (presentation only) ----
  // Steps are derived from what the organiser ACTUALLY shows — a field hidden in the config must not
  // appear in the progress bar (owner 2026-07-27: hidden fields were still counted as steps). Photo is a
  // step only when a profile picture is allowed (profilePicSource !== "none"); a field is a step only when
  // it exists in the config `fields` list.
  const nameVal = (values.name || "").trim();
  const companyVal = (values.company || "").trim();
  const captionVal = (caption || "").trim();
  const photoAllowed = (cfg.profilePicSource || "both") !== "none";
  const hasField = (k) => fields.some((f) => f.key === k);
  const steps = [
    ...(photoAllowed ? [{ key: "photo", label: "Photo", done: !!photoUrl }] : []),
    ...(hasField("name") ? [{ key: "name", label: "Name", done: !!nameVal }] : []),
    ...(hasField("company") ? [{ key: "company", label: "Company", done: !!companyVal }] : []),
    { key: "caption", label: "Caption", done: !!captionVal },
    { key: "share", label: "Share", done: !!shared },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const pct = steps.length ? Math.round((doneCount / steps.length) * 100) : 0;
  const detailsSummary = [nameVal, values.designation, companyVal].filter(Boolean).join(" · ") || "Add your details";

  // The event name for caption interpolation ("I'm attending X"). Falls back to a neutral phrase.
  const brand = cfg.shareTitle || "the event";
  // Hero headline: the organiser's own shareTitle WINS (their marketing control); otherwise a strong,
  // purpose-driven default so a bare config still reads like an ambassador CTA (owner feedback #3).
  const heroTitle = (cfg.shareTitle && cfg.shareTitle.trim()) || `Invite your network to ${brand}`;

  // A1: the organiser's CTA branding, with this page's existing look as the fallback.
  // `enabled:false` means the organiser turned the CTA off — that is about the EMAIL button, not
  // this page, so it only withdraws the custom styling here and never hides the share controls.
  const cta = parseCtaConfig(cfg.ctaConfig);
  const ctaOn = !!(cta && cta.enabled);
  const shareHeading = (ctaOn && cta.text) || "Share it";
  const stickyCopyStyle = ctaOn && cta.bgColor
    ? { ...S.stickyCopy, background: cta.bgColor, ...(cta.textColor ? { color: cta.textColor } : {}) }
    : S.stickyCopy;

  return (
    <div style={S.page}>
      <style>{CSS}</style>

      <div style={S.shell}>
        {/* topmini brand */}
        {show("brandBar") && (
          <div style={S.topmini}>
            <span style={S.brandDot}>◈</span>
            <span style={S.brandName}>{cfg.shareTitle || "Event Ambassador"}</span>
          </div>
        )}

        {shared ? (
          /* ================= SUCCESS VIEW ================= */
          <div style={S.phone}>
            {show("confetti") && <Confetti />}
            <div style={{ ...S.hero, background: "linear-gradient(160deg,#12351f22,#0f172a00)" }}>
              {show("successEyebrow") && (
                <div style={{ ...S.eyebrow, color: "#3ECF7B" }}>
                  <span style={{ ...S.eyebrowBadge, background: "#16A34A" }}>Shared</span> You're an ambassador now
                </div>
              )}
              <h1 style={S.h1}>Your card is live! 🎉</h1>
              {show("successCopy") && <p style={S.sub}>Thanks for spreading the word — you're on the leaderboard.</p>}
            </div>
            <div style={S.pad}>
              <div style={{ textAlign: "center" }}>
                <div className="successring" style={S.successRing}>✓</div>
                <h2 style={S.h2}>Posted to {shared}</h2>
                {show("successTracking") && (
                  <p style={{ color: "#94a3b8", margin: "6px 0 18px" }}>We'll track every click and registration from your post.</p>
                )}
                {show("successStats") && (
                  <div style={S.miniStat}>
                    <StatBox v={stats && stats.rank != null ? `#${stats.rank}` : "—"} k="Your rank" />
                    <StatBox v={stats && stats.successfulReferrals != null ? String(stats.successfulReferrals) : "0"} k="Referrals" />
                    <StatBox v="🥉" k="Next badge" />
                  </div>
                )}
                <button style={S.btnOutline} onClick={() => setShared(null)}>◈ Share to another platform</button>
                {show("successCopy") && <p style={S.note}>Top sharers win an Ambassador badge — check back to see your impact grow.</p>}
              </div>
            </div>
          </div>
        ) : (
          /* ================= BUILDER VIEW ================= */
          <div style={S.phone}>
            {/* SECTION 1+2 — HERO + big live preview (merged) */}
            <div style={S.hero}>
              {show("eyebrow") && (
                <div style={S.eyebrow}>
                  <span style={S.eyebrowBadge}>Ambassador</span> Represent the event
                </div>
              )}
              <h1 style={S.h1}>{heroTitle}</h1>
              {show("subtitle") && (
                <p style={S.sub}>{cfg.shareDescription || "Make your card & share it — every sign-up from your post is tracked to you."}</p>
              )}
              {photoUrl && (values.name || "").trim() && (
                <div style={S.imported}>✓ Name & photo ready</div>
              )}

              {/* big live preview (owner #1 — larger headline element) */}
              <div style={{ marginTop: 16 }}>
                <div style={S.previewTop}>
                  <span style={S.liveTag}><span className="pulse" style={S.pulse} /> Live preview</span>
                  {/* A2: shown ONLY when the organiser uploaded BOTH variants. With one there is
                      nothing to choose between, and a choice that cannot change the output was the
                      original defect. */}
                  {offerChoice && (
                    <span style={S.sizeToggle} role="group" aria-label="Card size">
                      {haveVariants.map((v) => (
                        <button
                          key={v}
                          style={activeVariant === v ? S.sizeOn : S.sizeBtn}
                          onClick={() => setSize(v)}
                        >
                          {v === "square" ? "Square" : v === "story" ? "Story" : "Landscape"}
                        </button>
                      ))}
                    </span>
                  )}
                </div>
                <div style={{ textAlign: "center" }}>
                  <canvas
                    ref={canvasRef}
                    width={cw}
                    height={ch}
                    style={{
                      width: "100%",
                      // A2: follow the REAL aspect being drawn, not the button. Portrait is capped
                      // narrow so a story does not run off a phone screen; a wide landscape card is
                      // allowed the full column, since its height is what stays small.
                      maxWidth: ch > cw ? 290 : cw > ch * 1.4 ? 460 : 400,
                      height: "auto",
                      aspectRatio: `${cw} / ${ch}`,
                      borderRadius: 16,
                      background: "#1e293b",
                      boxShadow: "0 20px 50px rgba(45,20,90,.35)",
                      transition: "max-width .15s cubic-bezier(.4,0,.2,1)",
                    }}
                  />
                </div>
              </div>

              {/* SECTION — leaderboard rank teaser. Reserved space (owner #4): render a "loading" rank
                  card as soon as we have a token, so the layout never jumps when the stats resolve. */}
              {show("rankCard") && token && (!stats || stats.rank != null) && (
                <div style={S.rankCard}>
                  <span style={S.medal}>🏆</span>
                  <span style={{ flex: 1 }}>
                    {stats && stats.rank != null ? (
                      <>
                        <span style={S.rankBig}>You're rank <b style={{ color: "#EAB308", fontFamily: "var(--eg-mono)" }}>#{stats.rank}</b></span>
                        <span style={S.rankSmall}>
                          {stats.successfulReferrals != null && `${stats.successfulReferrals} referral${stats.successfulReferrals === 1 ? "" : "s"} so far`}
                          {stats.referralsToNextRank ? ` · ${stats.referralsToNextRank} more to ${stats.nextMilestone || "climb"}` : ""}
                        </span>
                      </>
                    ) : (
                      <>
                        <span style={S.rankBig}>Your ambassador rank</span>
                        <span style={S.rankSmall}>Loading your leaderboard position…</span>
                      </>
                    )}
                  </span>
                </div>
              )}

              {/* completion bar (owner #2 — % label + clearer steps) */}
              {show("progress") && (
                <div style={{ marginTop: 14 }} aria-label="Ambassador setup progress">
                  <div style={S.progHead}>
                    <span style={S.progLabel}>Setup</span>
                    <span style={S.progPct}>{pct}% complete</span>
                  </div>
                  <div style={S.track}><div style={{ ...S.fill, width: `${pct}%` }} /></div>
                  <div style={S.steps}>
                    {steps.map((s) => (
                      <span key={s.key} style={{ ...S.step, color: s.done ? "#3ECF7B" : "#8E85A6" }}>
                        <span style={{ ...S.stepTick, background: s.done ? "#16A34A" : "rgba(148,163,184,.25)", color: s.done ? "#fff" : "#8E85A6" }}>
                          {s.done ? "✓" : ""}
                        </span>
                        {s.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={S.pad}>
              {/* SECTION 3 — PROFILE (collapsible accordion) */}
              <div className="acc" style={{ ...S.acc, ...(detailsOpen ? {} : {}) }}>
                <div style={S.accHead} onClick={() => setDetailsOpen((o) => !o)}>
                  <span style={{ ...S.accChk, background: doneCount >= 2 ? "#16A34A" : "rgba(148,163,184,.3)" }}>{doneCount >= 2 ? "✓" : "1"}</span>
                  <span style={{ flex: 1 }}>
                    <span style={S.accT}>Your details</span>
                    <span style={S.accS}>{detailsSummary}</span>
                  </span>
                  <span style={{ ...S.caret, transform: detailsOpen ? "rotate(180deg)" : "none" }}>▾</span>
                </div>
                {detailsOpen && (
                  <div style={S.accInner}>
                    {/* Photo */}
                    {(showUpload || showLinkedInPic) && (
                      <div style={S.field}>
                        <span style={S.label}>Your photo</span>
                        <div style={S.photoActions}>
                          {showUpload && (
                            <>
                              {/* Take Photo — opens a real in-app camera (getUserMedia). If no camera or the
                                  permission is denied, the modal explains it clearly and offers Upload instead. */}
                              <button type="button" className="pa" style={S.pa} onClick={() => setCameraOpen(true)}>
                                <span style={{ ...S.paIc, background: "#DB2777" }}>📷</span>
                                <span style={S.paT}>Take photo</span>
                                <span style={S.paS}>Use camera</span>
                              </button>
                              {/* Upload / choose from gallery — normal file picker (mobile shows gallery). */}
                              <label className="pa" style={S.pa}>
                                <span style={{ ...S.paIc, background: "#6D28D9" }}>🖼</span>
                                <span style={S.paT}>{photoUrl ? "Change" : "Upload"}</span>
                                <span style={S.paS}>Gallery · JPG/PNG</span>
                                <input ref={uploadInputRef} type="file" accept="image/*" onChange={(e) => onFile(e, setPhotoUrl)} style={{ display: "none" }} />
                              </label>
                            </>
                          )}
                          {showLinkedInPic && (
                            <button type="button" className="pa" style={S.pa} onClick={onUseLinkedInPhoto}>
                              <span style={{ ...S.paIc, background: "#0A66C2" }}>in</span>
                              <span style={S.paT}>From LinkedIn</span>
                              <span style={S.paS}>Photo + name</span>
                            </button>
                          )}
                        </div>
                        {photoUrl && (
                          <button type="button" style={S.removeBtn} onClick={() => setPhotoUrl(null)}>Remove photo</button>
                        )}
                        {!photoUrl && <p style={S.hint}>No photo? Your initials are used automatically.</p>}
                      </div>
                    )}

                    {/* Config-driven fields (organiser decides which; name/company/designation are the usual keys) */}
                    {fields.map((f) => {
                      const isName = f.key === "name";
                      return (
                        <div key={f.key} style={S.field}>
                          <span style={S.label}>
                            {f.label}{f.required ? " *" : ""}
                            {isName && liName && <span style={S.tagLi}>in From LinkedIn — editable</span>}
                          </span>
                          <input
                            value={values[f.key] || ""}
                            onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                            style={{ ...S.input, ...(isName && liName ? S.inputLi : {}) }}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* SECTION 4 — CAPTION + templates */}
              <div style={{ marginTop: 20 }}>
                <div style={S.sechead}><span style={S.sechN}>2</span> Your message</div>
                <div style={S.capChips}>
                  {/* "Yours" — restores the user's own typed message; only shown once they've written
                      something custom (so it never appears empty). Tapping a template preserves that draft. */}
                  {customDraft.trim() && (
                    <button
                      style={caption === customDraft ? S.capChipOn : S.capChip}
                      onClick={() => { setCaption(customDraft); setCaptionTouched(true); }}
                    >
                      ✎ Yours
                    </button>
                  )}
                  {CAPTION_TEMPLATES.map((t) => (
                    <button
                      key={t.key}
                      style={caption === t.text(brand) ? S.capChipOn : S.capChip}
                      onClick={() => {
                        // Before switching to a template, stash the user's own text so it isn't lost.
                        if (caption.trim() && !CAPTION_TEMPLATES.some((x) => x.text(brand) === caption)) {
                          setCustomDraft(caption);
                        }
                        setCaption(t.text(brand));
                        setCaptionTouched(true);
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <textarea
                  value={caption}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCaption(v);
                    setCaptionTouched(true);
                    // Track the user's own text (anything that isn't a template) as the restorable draft.
                    if (!CAPTION_TEMPLATES.some((x) => x.text(brand) === v)) setCustomDraft(v);
                  }}
                  rows={3}
                  maxLength={300}
                  style={{ ...S.input, resize: "none", lineHeight: 1.5 }}
                />
                <div style={S.charcount}>{caption.length}/300</div>
              </div>

              {exportErr && <p style={S.errBox}>{exportErr}</p>}

              {/* SECTION 5 — SHARE grid (secondary; primary CTA is sticky below) */}
              <div style={{ marginTop: 20 }}>
                <div style={S.sechead}><span style={S.sechN}>3</span> {shareHeading}</div>
                <div style={S.shareGrid}>
                  {/* AP-1: 1-tap LinkedIn auto-post. Rendered as a TILE inside the same grid as the
                      other platforms (owner 2026-08-15) so every share option is one consistent size
                      and row — it was previously a full-width block above the grid, which read as a
                      different kind of control. Still renders ONLY when the organiser toggle is on. */}
                  {cfg.autoPostLinkedin && !autoPostOff && (
                    <button style={S.sbtn} onClick={openAutoPostPreview} disabled={sharing}>
                      <span style={{ ...S.sbtnIc, background: PLATFORM_META.linkedin.bg }}>
                        {autoPosted ? "✓" : PLATFORM_META.linkedin.glyph}
                      </span>
                      <span style={S.sbtnL}>{autoPosted ? "Posted" : "Post for me"}</span>
                    </button>
                  )}
                  {allowed.map((h) => {
                    const m = PLATFORM_META[h] || { label: cap(h), bg: "#6D28D9", glyph: "◈" };
                    return (
                      <button key={h} style={S.sbtn} onClick={() => onShare(h)} disabled={sharing}>
                        <span style={{ ...S.sbtnIc, background: m.bg }}>{m.glyph}</span>
                        <span style={S.sbtnL}>{m.label}</span>
                      </button>
                    );
                  })}
                </div>
                {show("guidance") && (
                  <div style={S.fallback}>
                    <span style={{ fontSize: 17 }}>📸</span>
                    <div><b>LinkedIn, Facebook, Instagram:</b> we’ll guide you to post the card as a big image (far more reach than a small link) — tracked link stays in the caption.</div>
                  </div>
                )}
                {show("trackingNote") && (
                  <p style={S.note}>Your share link tracks how many people register from your post.</p>
                )}
              </div>

              {/* STICKY BAR (safe-area aware) — no longer duplicates the grid's platform buttons; instead
                  the two universal actions the grid DOESN'T have: copy the message + download the card. */}
              <div className="stickybar" style={S.stickybar}>
                <button style={{ ...S.stickyBtn, ...stickyCopyStyle }} onClick={() => copyForPlatform("copy")} disabled={sharing}>
                  {copied ? "✓ Copied!" : "⧉ Copy message"}
                </button>
                <button style={{ ...S.stickyBtn, ...S.stickyDl }} onClick={downloadPng} disabled={sharing}>
                  ↓ Download card
                </button>
              </div>
            </div>
          </div>
        )}

        {show("desktopHint") && (
          <p style={S.desktopHint}>📱 This ambassador page is designed mobile-first — it scales cleanly on desktop.</p>
        )}
      </div>

      {/* MANUAL-POST STEP MODAL — for platforms with no web share-intent (Instagram) or the generic
          "copy anywhere" flow. Guides Download → Copy → Open app, and PRESERVES per-platform attribution
          (the copy/download bake manual.platform into ?platform=). */}
      {manual && (
        <ManualPostModal
          platform={manual.platform}
          state={manual}
          copied={copied}
          onDownload={() => { downloadPng(); setManual((m) => m && { ...m, downloaded: true }); }}
          onCopy={async () => { await copyForPlatform(manual.platform); setManual((m) => m && { ...m, copied: true }); }}
          onClose={() => setManual(null)}
        />
      )}

      {/* AP-1: LinkedIn auto-post preview/consent modal (preview → publishing → done). */}
      {autoPost && (
        <AutoPostModal
          step={autoPost.step}
          postUrl={autoPost.postUrl}
          pngUrl={pngUrl}
          caption={interpolate(caption, captionTokens)}
          link={token ? landingUrl(token, "linkedin") : ""}
          onPublish={() => publishAutoPost()}
          onClose={() => setAutoPost(null)}
        />
      )}

      {/* In-app camera capture (getUserMedia). Handles no-camera / permission-denied gracefully. */}
      {cameraOpen && (
        <CameraModal
          onCapture={(dataUrl) => { setPhotoUrl(dataUrl); setCameraOpen(false); }}
          onUploadInstead={() => { setCameraOpen(false); uploadInputRef.current && uploadInputRef.current.click(); }}
          onClose={() => setCameraOpen(false)}
        />
      )}

      {/* Transient toast — guidance for platforms that don't auto-fill the caption. */}
      {toast && toast.msg && <div style={S.toast} role="status">{toast.msg}</div>}
    </div>
  );
}

/* In-app camera capture (getUserMedia). Opens the front camera, shows a live preview + a capture button.
   Handles the real failure modes clearly instead of silently falling back to a file picker:
     - no getUserMedia support / no camera hardware → "Camera Not Detected"
     - permission denied → "Allow Camera Permission" with a retry
   Always offers "Upload a photo instead" so the user is never stuck. */
function CameraModal({ onCapture, onUploadInstead, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const mountedRef = useRef(true);              // guards the async getUserMedia race (stop a late stream)
  const attemptedRef = useRef(false);           // true once we've asked for permission at least once
  const [status, setStatus] = useState("starting"); // starting | live | denied | nocamera | insecure | error
  const [busy, setBusy] = useState(false);

  const stop = useCallback(() => {
    const s = streamRef.current;
    if (s) { s.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  }, []);

  const start = useCallback(async () => {
    setStatus("starting");
    // Camera requires a secure context (HTTPS/localhost). On plain HTTP getUserMedia is unavailable OR
    // rejects with SecurityError — tell the user the truth instead of an unsatisfiable "allow camera".
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    const secure = typeof window !== "undefined" && (window.isSecureContext || host === "localhost" || host === "127.0.0.1");
    if (!secure) { setStatus("insecure"); return; }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) { setStatus("nocamera"); return; }
    attemptedRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1080 }, height: { ideal: 1080 } },
        audio: false,
      });
      // If the user already closed the modal while getUserMedia was resolving, stop the stream NOW —
      // otherwise the camera track stays live and the camera light stays on ("app is spying" reports).
      if (!mountedRef.current) { stream.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      if (mountedRef.current) setStatus("live");
    } catch (err) {
      if (!mountedRef.current) return;
      const name = err && err.name;
      if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") setStatus("denied");
      else if (name === "NotFoundError" || name === "DevicesNotFoundError" || name === "OverconstrainedError") setStatus("nocamera");
      else setStatus("error");
    }
  }, [stop]);

  useEffect(() => {
    mountedRef.current = true;
    start();
    return () => { mountedRef.current = false; stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const capture = () => {
    const v = videoRef.current;
    if (!v || status !== "live") return;
    setBusy(true);
    try {
      const w = v.videoWidth || 1080;
      const h = v.videoHeight || 1080;
      const side = Math.min(w, h); // center-crop to a square
      const c = document.createElement("canvas");
      c.width = side; c.height = side;
      const ctx = c.getContext("2d");
      ctx.drawImage(v, (w - side) / 2, (h - side) / 2, side, side, 0, 0, side, side);
      const dataUrl = c.toDataURL("image/jpeg", 0.9);
      stop();
      onCapture(dataUrl);
    } catch {
      setStatus("error");
    } finally {
      setBusy(false);
    }
  };

  const close = () => { stop(); onClose(); };
  const uploadInstead = () => { stop(); onUploadInstead(); }; // ALWAYS stop the camera before leaving

  // After a HARD permission deny the browser won't re-prompt, so "Try again" only helps if the user has
  // since changed the site permission. We only show it when a retry can plausibly work.
  const message =
    status === "insecure"
      ? { icon: "🔒", title: "Camera Needs a Secure Connection", body: "The camera only works over a secure (https) connection. Please upload a photo instead.", retry: false }
      : status === "denied"
        ? { icon: "🔒", title: "Allow Camera Permission", body: "Camera access is blocked. Tap the camera/lock icon in your browser's address bar, allow the camera for this site, then tap Try again — or just upload a photo.", retry: true }
        : status === "nocamera"
          ? { icon: "📷", title: "Camera Not Detected", body: "We couldn't find a camera on this device. You can upload a photo instead.", retry: false }
          : status === "error"
            ? { icon: "⚠️", title: "Camera Unavailable", body: "The camera couldn't start. Try again, or upload a photo instead.", retry: true }
            : null;

  return (
    <div style={MS.overlay} onClick={close} role="dialog" aria-modal="true">
      <div style={MS.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={MS.head}>
          <span style={{ ...MS.badge, background: "#DB2777" }}>📷</span>
          <span style={MS.title}>Take a photo</span>
          <button style={MS.close} onClick={close} aria-label="Close">✕</button>
        </div>

        {message ? (
          <div style={{ textAlign: "center", padding: "18px 8px 4px" }}>
            <div style={{ fontSize: 34, marginBottom: 8 }}>{message.icon}</div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>{message.title}</div>
            <div style={{ ...MS.lead, textAlign: "center" }}>{message.body}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
              {/* Upload is the RELIABLE primary action; Try-again is secondary (may be a no-op after a hard deny). */}
              <button style={{ ...MS.stepBtn, ...MS.stepBtnPrimary }} onClick={uploadInstead}>🖼 Upload a photo instead</button>
              {message.retry ? (
                <button style={MS.stepBtn} onClick={start}>↺ Try again</button>
              ) : null}
            </div>
          </div>
        ) : (
          <>
            <div style={{ position: "relative", borderRadius: 14, overflow: "hidden", background: "#000", aspectRatio: "1 / 1", marginTop: 6 }}>
              <video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" }} />
              {status === "starting" && (
                <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "#fff", fontSize: 13 }}>Starting camera…</div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "center" }}>
              <button style={{ ...MS.stepBtn, ...MS.stepBtnPrimary }} onClick={capture} disabled={status !== "live" || busy}>
                ● Capture
              </button>
              <button style={MS.stepBtn} onClick={uploadInstead}>Upload instead</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* Manual-post guidance modal (Instagram / "copy anywhere"). Three numbered steps; the primary action per
   step lights up as done. Attribution is handled by the caller (copyForPlatform(platform)). */
function ManualPostModal({ platform, state, copied, onDownload, onCopy, onClose }) {
  const meta = PLATFORM_META[platform] || { label: platform === "copy" ? "anywhere" : cap(platform), bg: "#6D28D9", glyph: "◈" };
  const isCopyAnywhere = platform === "copy";
  return (
    <div style={MS.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div style={MS.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={MS.head}>
          <span style={{ ...MS.badge, background: meta.bg }}>{meta.glyph}</span>
          <span style={MS.title}>{isCopyAnywhere ? "Copy & share anywhere" : `Post to ${meta.label}`}</span>
          <button style={MS.close} onClick={onClose} aria-label="Close">✕</button>
        </div>
        <p style={MS.lead}>
          {isCopyAnywhere
            ? "Your caption + tracked link are ready — paste them wherever you like."
            : (meta.label === "LinkedIn" || meta.label === "Facebook")
              ? `Post your card as a BIG image on ${meta.label} (not a small link) in 3 quick steps — this gets far more reach:`
              : `${meta.label} can't be opened with a prefilled post, so post it yourself in 3 quick steps:`}
        </p>
        {/* If the card couldn't be exported (tainted template), tell the user HERE — not behind the modal. */}
        {state.exportErr ? (
          <div style={{ background: "rgba(245,158,11,.12)", border: "1px solid rgba(245,158,11,.5)", color: "#fbbf24", borderRadius: 10, padding: "10px 12px", fontSize: 12.5, lineHeight: 1.45, marginBottom: 12 }}>
            {state.exportErr}
          </div>
        ) : null}
        <div style={MS.steps}>
          {!isCopyAnywhere && (
            <div style={MS.step}>
              <span style={{ ...MS.num, ...(state.downloaded ? MS.numDone : {}) }}>{state.downloaded ? "✓" : "1"}</span>
              <div style={{ flex: 1 }}>
                <div style={MS.stepT}>Download your card</div>
                <button style={{ ...MS.stepBtn, ...MS.stepBtnPrimary }} onClick={onDownload}>↓ Download card</button>
              </div>
            </div>
          )}
          <div style={MS.step}>
            <span style={{ ...MS.num, ...(copied || state.copied ? MS.numDone : {}) }}>{copied || state.copied ? "✓" : isCopyAnywhere ? "1" : "2"}</span>
            <div style={{ flex: 1 }}>
              <div style={MS.stepT}>Copy your caption + link</div>
              <button style={{ ...MS.stepBtn, ...MS.stepBtnPrimary }} onClick={onCopy}>{copied ? "✓ Copied!" : "⧉ Copy message"}</button>
            </div>
          </div>
          <div style={MS.step}>
            <span style={MS.num}>{isCopyAnywhere ? "2" : "3"}</span>
            <div style={{ flex: 1 }}>
              <div style={MS.stepT}>
                {isCopyAnywhere ? "Paste it in your post" : `Open ${meta.label} → new post → attach the card → paste`}
              </div>
              <div style={MS.stepHint}>
                {meta.label === "LinkedIn" || meta.label === "Facebook"
                  ? `Start a NEW post, click the photo/image icon and attach the card you just downloaded, then paste your caption. Posting the card as an IMAGE (not a link) is what makes it show big — your tracked link stays in the caption, so every sign-up counts for you.`
                  : meta.label === "Instagram"
                    ? "In Instagram: create a post, attach the downloaded card, and paste your caption. Your tracked link stays in the caption — every sign-up counts for you."
                    : `In ${meta.label}: start a new post and paste (long-press → Paste). Attach the downloaded card too. Your tracked link stays in the caption — every sign-up counts for you.`}
              </div>
              {!isCopyAnywhere && APP_OPEN_URL[platform] && (
                <a
                  href={APP_OPEN_URL[platform]}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ ...MS.stepBtn, ...MS.stepBtnPrimary, textDecoration: "none", display: "inline-block" }}
                >
                  ↗ Open {meta.label}
                </a>
              )}
            </div>
          </div>
        </div>
        <button style={MS.done} onClick={onClose}>Done</button>
      </div>
    </div>
  );
}

/* AP-1 — the auto-post consent/preview modal (UX_SPEC_LINKEDIN_AUTOPOST.md A3). Shows the EXACT post
   (banner + caption + tracked link) and publishes ONLY on the explicit Publish tap. While publishing,
   close/overlay dismissal is disabled so a slow API can't be double-fired or half-abandoned. */
function AutoPostModal({ step, postUrl, pngUrl, caption, link, onPublish, onClose }) {
  const publishing = step === "publishing";
  return (
    <div style={MS.overlay} onClick={publishing ? undefined : onClose} role="dialog" aria-modal="true">
      <div style={MS.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={MS.head}>
          <span style={{ ...MS.badge, background: "#0A66C2" }}>in</span>
          <span style={MS.title}>{step === "done" ? "Posted to LinkedIn!" : "Ready to post?"}</span>
          <button
            style={{ ...MS.close, ...(publishing ? { opacity: 0.3, cursor: "default" } : {}) }}
            onClick={publishing ? undefined : onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        {step === "done" ? (
          <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
            <div style={{ fontSize: 40 }}>🎉</div>
            <p style={{ ...MS.lead, marginTop: 6 }}>Your post is live — banner, caption and your tracked link.</p>
            {postUrl && (
              <a
                href={postUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...MS.stepBtn, ...MS.stepBtnPrimary, textDecoration: "none", display: "inline-block", marginBottom: 6 }}
              >
                ↗ View your post
              </a>
            )}
            <button style={MS.done} onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            {pngUrl ? (
              <img
                src={pngUrl}
                alt="Your banner"
                style={{ width: "100%", maxHeight: "38vh", objectFit: "contain", borderRadius: 10, background: "rgba(255,255,255,.04)" }}
              />
            ) : null}
            <div style={{ background: "rgba(255,255,255,.06)", borderRadius: 10, padding: "10px 12px", fontSize: 13.5, lineHeight: 1.5, margin: "12px 0 6px", whiteSpace: "pre-wrap" }}>
              {caption}
            </div>
            <div style={{ fontSize: 11, opacity: 0.7, wordBreak: "break-all", fontFamily: "ui-monospace, Menlo, monospace" }}>{link}</div>
            <div style={{ borderLeft: "3px solid #0A66C2", background: "rgba(10,102,194,.14)", padding: "9px 12px", borderRadius: "0 8px 8px 0", fontSize: 12.5, lineHeight: 1.45, margin: "12px 0" }}>
              This will publish this exact post publicly on your LinkedIn profile — nothing is ever posted without you pressing Publish.
            </div>
            <button
              style={{ ...MS.done, background: "#0A66C2", color: "#fff", opacity: publishing ? 0.85 : 1 }}
              onClick={publishing ? undefined : onPublish}
              disabled={publishing}
            >
              {publishing ? "Publishing to your LinkedIn…" : "Publish to LinkedIn"}
            </button>
            <p style={{ fontSize: 11.5, opacity: 0.65, marginTop: 10, lineHeight: 1.45 }}>
              Posting as the LinkedIn account you just connected. One post per connection — we discard the permission right after publishing.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------- small presentational sub-components ---------- */
function StatBox({ v, k }) {
  return (
    <div style={S.statBox}>
      <div style={S.statV}>{v}</div>
      <div style={S.statK}>{k}</div>
    </div>
  );
}
function Confetti() {
  const cols = ["#6D28D9", "#DB2777", "#16A34A", "#0EA5E9", "#D97706"];
  const pieces = Array.from({ length: 44 });
  return (
    <div className="confetti" aria-hidden="true">
      {pieces.map((_, i) => (
        <i
          key={i}
          style={{
            left: `${(i * 2.29) % 100}%`,
            background: cols[i % 5],
            animationDelay: `${(i % 10) * 0.05}s`,
            animationDuration: `${2.2 + (i % 6) * 0.2}s`,
          }}
        />
      ))}
    </div>
  );
}

/* caption templates (2B #7) — one-tap, brand-interpolated, still fully editable after. */
const CAPTION_TEMPLATES = [
  { key: "pro", label: "Professional", text: (b) => `Excited to be attending ${b} — let's connect!` },
  { key: "friendly", label: "Friendly", text: (b) => `Can't wait for ${b}! 🎉 Come say hi if you're going too 👋` },
  { key: "short", label: "Short", text: (b) => `See you at ${b}. Register here 👇` },
  { key: "invite", label: "Invite", text: (b) => `I'm attending ${b} — join me and register today 👇` },
];

/* $-style token aliases (owner 2026-08-06): organisers write EMAIL-style tokens in the default
   message — "Visit our stand $stallNo", "$First Name", "$stand number" — so captions must resolve
   them just like {curly} tokens. Per field, patterns run MOST-SPECIFIC-FIRST ("stall number" before
   bare "stall") so a longer alias is never half-eaten; `[\s_]*` makes spaces/underscores/camel-case
   all match ("$stallNo" / "$stall_no" / "$Stall No"); matching is case-insensitive. A $word that
   matches NO alias is left exactly as typed (never destroyed). */
const DOLLAR_ALIASES = [
  ["stallNo", ["stall[\\s_]*number", "stand[\\s_]*number", "booth[\\s_]*number", "stall[\\s_]*no", "stand[\\s_]*no", "booth[\\s_]*no", "stall", "booth"]],
  ["name", ["first[\\s_]*name", "full[\\s_]*name", "name"]],
  ["company", ["company[\\s_]*name", "organisation", "organization", "company"]],
  ["designation", ["job[\\s_]*title", "designation", "position"]],
  ["eventName", ["event[\\s_]*name", "event[\\s_]*title", "event"]],
];

/* Resolve {token} + $alias placeholders in a caption to live values, applied ONLY at share-time
   (egress) and in the share previews — the editable caption + the template-chip equality checks keep
   the raw text. Unknown/blank tokens → "" (with doubled spaces collapsed so a missing value never
   leaves a hole). The 4 default templates contain NO tokens, so for them this is a provable no-op. */
function interpolate(str, tokens) {
  if (!str) return str;
  let out = str.replace(/\{(\w+)\}/g, (_, k) => (tokens && tokens[k] != null ? String(tokens[k]) : ""));
  for (const [key, patterns] of DOLLAR_ALIASES) {
    const val = tokens && tokens[key] != null ? String(tokens[key]).trim() : "";
    for (const p of patterns) {
      out = out.replace(new RegExp(`\\$${p}\\b`, "gi"), val);
    }
  }
  return out.replace(/ {2,}/g, " ");
}

/* ---------- canvas + input helpers (UNCHANGED business logic) ---------- */
function safeParse(s, fb) {
  if (!s) return fb;
  if (typeof s === "object") return s;
  try {
    return JSON.parse(s);
  } catch {
    return fb;
  }
}

// Shape guards for organiser-config JSON. `safeParse` guarantees only that the text PARSED — not that
// it parsed into what the caller is about to use. `null`, `42` and `"text"` are all valid JSON, and
// each of them crashes a `.photo` / `.some(...)` access. These make a malformed column render an
// empty banner instead of white-screening the page.
function asObject(v, fb) {
  return v && typeof v === "object" && !Array.isArray(v) ? v : fb;
}
function asArray(v, fb) {
  return Array.isArray(v) ? v : fb;
}
function loadImg(url) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = url;
  });
}
function bg(ctx, w, h) {
  const g = ctx.createLinearGradient(0, 0, w || CANVAS, h || CANVAS);
  g.addColorStop(0, "#2b1668");
  g.addColorStop(0.6, "#6D28D9");
  g.addColorStop(1, "#DB2777");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w || CANVAS, h || CANVAS);
}
function clipPhoto(ctx, x, y, w, shape, radius) {
  if (shape === "square") return;
  ctx.beginPath();
  if (shape === "rounded") {
    const r = Math.min(radius, w / 2);
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + w, r);
    ctx.arcTo(x + w, y + w, x, y + w, r);
    ctx.arcTo(x, y + w, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  } else {
    ctx.arc(x + w / 2, y + w / 2, w / 2, 0, Math.PI * 2);
  }
  ctx.clip();
}
function photoPlaceholder(ctx, x, y, w) {
  ctx.fillStyle = "rgba(148,163,184,0.45)";
  ctx.fillRect(x, y, w, w);
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.beginPath();
  ctx.arc(x + w / 2, y + w * 0.38, w * 0.18, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(x + w / 2, y + w * 0.95, w * 0.32, Math.PI, 2 * Math.PI);
  ctx.fill();
}
// Deterministic initials avatar (like ui-avatars) — a coloured square + the name's initials. The
// caller has already clipped to the photo shape, so this fills the frame. Colour is derived from the
// name so the same person always gets the same avatar.
const AVATAR_COLORS = ["#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6"];
function initialsAvatar(ctx, x, y, w, name) {
  const clean = String(name || "").trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  const initials = ((parts[0] || "").charAt(0) + (parts[1] || "").charAt(0)).toUpperCase() || "?";
  let hash = 0;
  for (let i = 0; i < clean.length; i++) hash = (hash * 31 + clean.charCodeAt(i)) & 0x7fffffff;
  ctx.fillStyle = AVATAR_COLORS[hash % AVATAR_COLORS.length];
  ctx.fillRect(x, y, w, w);
  ctx.fillStyle = "#fff";
  ctx.font = `700 ${Math.round(w * 0.42)}px Arial`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(initials, x + w / 2, y + w / 2 + w * 0.02);
}
// Wrap `str` into lines that each fit within maxWidth (canvas px), breaking on spaces; a single word
// longer than maxWidth is hard-broken by character so it can never overflow the box. ctx.font must be set.
function wrapLines(ctx, str, maxWidth) {
  if (!maxWidth || maxWidth <= 0) return [str];
  const words = String(str).split(/\s+/);
  const lines = [];
  let line = "";
  const pushWord = (w) => {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width <= maxWidth) {
      line = test;
    } else {
      if (line) lines.push(line);
      // Word itself too wide → hard-break by character.
      if (ctx.measureText(w).width > maxWidth) {
        let chunk = "";
        for (const ch of w) {
          if (ctx.measureText(chunk + ch).width <= maxWidth) chunk += ch;
          else { if (chunk) lines.push(chunk); chunk = ch; }
        }
        line = chunk;
      } else {
        line = w;
      }
    }
  };
  for (const w of words) pushWord(w);
  if (line) lines.push(line);
  return lines.length ? lines : [str];
}

// Draw a text field. Owner 2026-08-05: honors box.align (left|center|right), box.weight/bold, box.font,
// box.width (wrap to that pixel width → multi-line), box.prefix/postfix, and returns the pixel HEIGHT it
// occupied so the caller can push a following field down (collision avoidance). x/y is the anchor; y is the
// vertical CENTER of the first line (back-compat with the old single-line middle baseline).
function text(ctx, val, box) {
  if (!val || !box) return 0;
  const display = `${box.prefix || ""}${val}${box.postfix || ""}`;
  const size = box.size || 44;
  const lineH = Math.round(size * 1.2);
  const weight = box.bold ? "bold" : box.weight || "normal"; // bold checkbox OR explicit weight (e.g. 600)
  ctx.save();
  ctx.fillStyle = box.color || "#fff";
  ctx.font = `${weight} ${size}px ${box.font || "Arial"}`;
  ctx.textAlign = box.align || "center";
  ctx.textBaseline = "middle";
  // AUTO-WRAP (owner 2026-08-06): a long value (e.g. "Eventegration Technologies") used to run
  // straight OFF the canvas edge whenever the organiser's layout box had no explicit width — the old
  // rule was "no width → single line". Now, when no width is set, derive the available width from the
  // box's x + alignment against the CANVAS bounds, so long text always wraps to the next line instead
  // of clipping. Text that already fit renders identically (wrapLines returns one line), and the
  // caller's collision push-down (2026-08-05) moves the fields below to make room. An organiser-set
  // box.width still wins exactly as before.
  const EDGE = 24; // keep wrapped text off the canvas edge
  const cwCanvas = ctx.canvas.width;
  let maxW = box.width;
  if (!maxW) {
    const align = box.align || "center";
    if (align === "left") maxW = cwCanvas - box.x - EDGE;
    else if (align === "right") maxW = box.x - EDGE;
    else maxW = 2 * Math.min(box.x, cwCanvas - box.x) - EDGE;
    maxW = Math.max(40, maxW);
  }
  const lines = wrapLines(ctx, display, maxW);
  lines.forEach((ln, i) => ctx.fillText(ln, box.x, box.y + i * lineH));
  ctx.restore();
  return lineH * lines.length; // occupied height, for collision push-down
}
function onFile(e, setUrl) {
  const f = e.target.files && e.target.files[0];
  if (!f || !f.type.startsWith("image/") || f.size > 5 * 1024 * 1024) return;
  const reader = new FileReader();
  reader.onload = () => setUrl(String(reader.result));
  reader.readAsDataURL(f);
}
function cap(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
function Center({ children }) {
  return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--eg-ground,#141021)", color: "#94a3b8", fontFamily: "var(--eg-body,system-ui)" }}>{children}</div>;
}

/* ---------- animation/hover/media CSS that inline styles can't express ---------- */
const CSS = `
:root{--eg-ground:#141021;--eg-primary:#9B6BF2;--eg-mono:'JetBrains Mono',ui-monospace,monospace;--eg-body:'Outfit',system-ui,sans-serif;--eg-display:'Fraunces',Georgia,serif;}
.pa{transition:border-color .12s,background .12s;}
.pa:hover{border-color:#9B6BF2 !important;background:rgba(155,107,242,.1) !important;}
.pulse{animation:egpulse 1.6s ease infinite;}
@keyframes egpulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.4;transform:scale(.8)}}
.successring{animation:egpop 420ms cubic-bezier(.16,1,.3,1);}
@keyframes egpop{0%{transform:scale(.5);opacity:0}60%{transform:scale(1.08)}100%{transform:scale(1);opacity:1}}
.confetti{position:fixed;inset:0;pointer-events:none;z-index:5;overflow:hidden;}
.confetti i{position:absolute;top:-12px;width:8px;height:12px;border-radius:2px;animation:egfall 2.6s linear forwards;}
@keyframes egfall{to{transform:translateY(108vh) rotate(680deg);opacity:0}}
.stickybar{position:sticky;bottom:0;}
@media (prefers-reduced-motion:reduce){*{animation:none!important}}
`;

/* ---------- style objects (dark ambassador theme, SPE-aligned) ---------- */
/*
 * A1 — the organiser's CTA config, as edited in Reception's Share Generator.
 *
 * The same JSON the EMAIL "Share You're Attending" button is built from. Until now api-reg never
 * published it, so this page drew its own label and colour and a visitor arriving from a branded
 * pink email landed on a page that shared none of that branding.
 *
 * PRIORITY: config wins; the existing hardcoded values remain the fallback for every field, so a
 * null/absent/garbage config renders exactly what this page rendered before. Colours are validated
 * the same way the email builder validates them — an unparseable value falls back rather than
 * reaching the DOM as a style.
 */
/*
 * A2 — the banner VARIANTS the organiser actually uploaded.
 *
 * The Square/Story buttons used to read `size` in exactly ONE place: the width of the preview box.
 * Picking "Square" on a story event produced a narrower story, never a square — the canvas, the
 * artwork and the download were always the organiser's single configured aspect. Now the choice
 * selects a real source image AND a real canvas, and it is only offered when there is genuinely
 * something to choose between.
 *
 * `banners` comes from the server, which applies the same rule Reception's editor applies — an
 * event that predates A2 reports exactly one variant taken from its legacy bannerTemplateUrl at its
 * configured aspect, so it renders exactly as it always has and offers no chooser.
 */
/*
 * A2 — ONE draw rule, shared with Reception's BannerCanvas so the preview and the export agree.
 *
 * The old `drawImage(img, 0, 0, cw, ch)` passed no source rect, so the artwork was STRETCHED to
 * whatever the configured canvas was. Measured against production, 6 of 11 configured banners had a
 * canvas whose aspect did not match the file, and every one of them shipped distorted.
 *
 * COVER (centre-crop) rather than contain: a share card with letterbox bars reads as broken, and
 * the organiser's artwork is designed to bleed to the edges. Aspect is always preserved.
 */
function drawCover(ctx, img, cw, ch) {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) { ctx.drawImage(img, 0, 0, cw, ch); return; }   // unknowable: old behaviour
  const scale = Math.max(cw / iw, ch / ih);
  const dw = iw * scale;
  const dh = ih * scale;
  ctx.drawImage(img, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
}

function bannerVariants(cfg) {
  const b = (cfg && cfg.banners) || null;
  const one = (v) => {
    const x = b && b[v];
    if (x && x.url) return { url: x.url, w: Number(x.width) || 0, h: Number(x.height) || 0 };
    return null;
  };
  const set = { square: one("square"), story: one("story"), landscape: one("landscape") };
  if (set.square || set.story || set.landscape) return set;
  // Server too old to publish `banners` (or none configured): fall back to the single banner
  // exactly as this page did before A2.
  const url = cfg && cfg.bannerTemplateUrl;
  if (!url) return set;
  const w = Number(cfg.bannerWidth) || CANVAS;
  const h = Number(cfg.bannerHeight) || CANVAS;
  set[h > w ? "story" : "square"] = { url, w, h };
  return set;
}
const variantsOf = (set) => ["square", "story", "landscape"].filter((v) => set[v]);

const CTA_COLOR_RE = /^#[0-9A-Fa-f]{3,8}$|^rgb/;
function parseCtaConfig(raw) {
  if (!raw) return null;
  try {
    const c = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!c || typeof c !== "object") return null;
    const color = (v) => (CTA_COLOR_RE.test(String(v || "").trim()) ? String(v).trim() : null);
    return {
      enabled: c.enabled != null ? !!c.enabled : true,
      text: (c.text ? String(c.text).trim() : "") || null,
      bgColor: color(c.bgColor),
      textColor: color(c.textColor),
    };
  } catch {
    return null;   // malformed JSON must never break the page — fall back to the hardcoded look
  }
}

const C = {
  ground: "#141021", surface: "#1E1830", surface2: "#251E3A", surface3: "#2E2646",
  ink: "#F3F0FA", ink2: "#C3BAD8", ink3: "#8E85A6", line: "#332B4A", line2: "#3F3658",
  primary: "#9B6BF2", primarySoft: "#2A1F45", accent: "#F06CAB", reg: "#3ECF7B", regSoft: "#12351F",
  gold: "#EAB308", goldSoft: "#3A2E0A",
  display: "'Fraunces',Georgia,serif", body: "'Outfit',system-ui,sans-serif", mono: "'JetBrains Mono',ui-monospace,monospace",
};
const S = {
  page: { minHeight: "100vh", background: C.ground, color: C.ink, padding: "20px 14px 40px", fontFamily: C.body, WebkitFontSmoothing: "antialiased" },
  shell: { maxWidth: 452, margin: "0 auto", display: "flex", flexDirection: "column", alignItems: "center" },
  topmini: { display: "flex", alignItems: "center", gap: 8, width: "100%", marginBottom: 14 },
  brandDot: { width: 26, height: 26, borderRadius: 8, background: "linear-gradient(135deg,#9B6BF2,#F06CAB)", display: "grid", placeItems: "center", color: "#fff", fontSize: 13 },
  brandName: { fontFamily: C.display, fontWeight: 600, fontSize: 15 },
  phone: { width: "100%", background: C.surface, border: `1px solid ${C.line}`, borderRadius: 22, boxShadow: "0 14px 36px rgba(0,0,0,.5)", overflow: "hidden", position: "relative" },
  hero: { position: "relative", background: "linear-gradient(160deg,#2A1F45,#1E1830 95%)", padding: "20px 22px 22px", overflow: "hidden" },
  eyebrow: { fontFamily: C.display, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".14em", color: C.primary, display: "inline-flex", alignItems: "center", gap: 7, marginBottom: 9 },
  eyebrowBadge: { background: C.primary, color: "#1a0f2e", borderRadius: 999, padding: "2px 9px", fontSize: 10, letterSpacing: ".08em" },
  h1: { fontFamily: C.display, fontWeight: 600, fontSize: 23, lineHeight: 1.08, margin: 0, letterSpacing: "-.01em" },
  h2: { fontFamily: C.display, fontWeight: 600, fontSize: 22, margin: 0 },
  sub: { color: C.ink2, fontSize: 13.5, margin: "6px 0 0" },
  imported: { display: "inline-flex", alignItems: "center", gap: 6, marginTop: 11, fontSize: 11.5, fontWeight: 600, color: C.reg, background: C.regSoft, borderRadius: 999, padding: "4px 11px" },
  previewTop: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 },
  liveTag: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, color: C.primary, background: "rgba(30,24,48,.7)", border: `1px solid ${C.line2}`, borderRadius: 999, padding: "3px 10px" },
  pulse: { width: 7, height: 7, borderRadius: "50%", background: C.reg, display: "inline-block" },
  sizeToggle: { display: "inline-flex", background: "rgba(30,24,48,.7)", border: `1px solid ${C.line}`, borderRadius: 999, padding: 3, gap: 2 },
  sizeBtn: { border: "none", background: "transparent", color: C.ink3, fontFamily: C.body, fontSize: 11, fontWeight: 600, padding: "4px 11px", borderRadius: 999, cursor: "pointer" },
  sizeOn: { border: "none", background: C.surface, color: C.primary, fontFamily: C.body, fontSize: 11, fontWeight: 600, padding: "4px 11px", borderRadius: 999, cursor: "pointer", boxShadow: "0 1px 2px rgba(0,0,0,.3)" },
  rankCard: { marginTop: 14, display: "flex", alignItems: "center", gap: 12, background: "rgba(30,24,48,.82)", border: `1px solid ${C.gold}55`, borderRadius: 12, padding: "11px 14px" },
  medal: { width: 38, height: 38, borderRadius: 11, background: C.goldSoft, color: C.gold, display: "grid", placeItems: "center", fontSize: 19, flexShrink: 0 },
  rankBig: { display: "block", fontFamily: C.display, fontWeight: 700, fontSize: 15, lineHeight: 1.1 },
  rankSmall: { display: "block", fontSize: 11.5, color: C.ink2, marginTop: 1 },
  progHead: { display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 7 },
  progLabel: { fontFamily: C.display, fontWeight: 600, fontSize: 12.5, color: C.ink2 },
  progPct: { fontFamily: C.mono, fontWeight: 700, fontSize: 12.5, color: C.primary },
  track: { height: 8, borderRadius: 4, background: "rgba(243,240,250,.09)", overflow: "hidden" },
  fill: { height: "100%", borderRadius: 4, background: "linear-gradient(90deg,#9B6BF2,#F06CAB)", transition: "width .25s cubic-bezier(.16,1,.3,1)" },
  steps: { display: "flex", gap: 11, marginTop: 9, flexWrap: "wrap" },
  step: { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600 },
  stepTick: { width: 15, height: 15, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: 9 },
  pad: { padding: "20px 22px 22px" },
  sechead: { fontFamily: C.display, fontWeight: 600, fontSize: 15, margin: "0 0 12px", display: "flex", alignItems: "center", gap: 8 },
  sechN: { width: 20, height: 20, borderRadius: 6, background: C.primarySoft, color: C.primary, display: "grid", placeItems: "center", fontSize: 11, fontWeight: 700, fontFamily: C.mono },
  acc: { border: `1px solid ${C.line}`, borderRadius: 12, overflow: "hidden" },
  accHead: { display: "flex", alignItems: "center", gap: 9, padding: "13px 15px", cursor: "pointer" },
  accChk: { width: 22, height: 22, borderRadius: "50%", color: "#fff", display: "grid", placeItems: "center", fontSize: 12, flexShrink: 0 },
  accT: { display: "block", fontFamily: C.display, fontWeight: 600, fontSize: 14.5 },
  accS: { display: "block", fontSize: 11.5, color: C.ink3, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 300 },
  caret: { color: C.ink3, fontSize: 13, transition: "transform .15s" },
  accInner: { padding: "4px 15px 15px" },
  field: { marginBottom: 13 },
  label: { fontSize: 12.5, fontWeight: 600, color: C.ink2, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 },
  tagLi: { color: "#4CBBF0", fontWeight: 600, fontSize: 11 },
  input: { fontFamily: C.body, fontSize: 15, padding: "11px 13px", border: `1px solid ${C.line2}`, borderRadius: 9, background: C.surface2, color: C.ink, width: "100%", boxSizing: "border-box", outline: "none" },
  inputLi: { borderColor: "#0A66C2", boxShadow: "0 0 0 3px rgba(10,102,194,.25)" },
  charcount: { fontSize: 11, color: C.ink3, textAlign: "right", marginTop: 4 },
  photoActions: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(96px, 1fr))", gap: 10 },
  pa: { display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "14px 10px", border: `1.5px dashed ${C.line2}`, borderRadius: 12, cursor: "pointer", background: C.surface, textAlign: "center", color: C.ink },
  paIc: { width: 36, height: 36, borderRadius: 11, display: "grid", placeItems: "center", color: "#fff", fontSize: 17, fontWeight: 700 },
  paT: { fontSize: 12.5, fontWeight: 600 },
  paS: { fontSize: 11, color: C.ink3 },
  removeBtn: { gridColumn: "1 / -1", padding: "8px 14px", borderRadius: 9, border: `1px solid #7f1d1d`, background: "transparent", color: "#f87171", fontSize: 12.5, cursor: "pointer", fontWeight: 600 },
  hint: { margin: "6px 0 0", fontSize: 11, color: C.ink3 },
  capChips: { display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 9 },
  capChip: { fontFamily: C.body, fontSize: 12, fontWeight: 600, color: C.ink2, background: C.surface, border: `1px solid ${C.line2}`, borderRadius: 999, padding: "5px 12px", cursor: "pointer" },
  capChipOn: { fontFamily: C.body, fontSize: 12, fontWeight: 600, color: C.primary, background: C.primarySoft, border: `1px solid ${C.primary}`, borderRadius: 999, padding: "5px 12px", cursor: "pointer" },
  errBox: { marginTop: 12, fontSize: 12, color: "#fca5a5", background: "rgba(239,68,68,.12)", border: "1px solid rgba(239,68,68,.3)", borderRadius: 8, padding: "8px 10px" },
  shareGrid: { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 },
  sbtn: { display: "flex", flexDirection: "column", alignItems: "center", gap: 7, cursor: "pointer", border: "none", background: "none", color: C.ink2, fontFamily: C.body },
  // AP-1: the 1-tap LinkedIn auto-post button (rendered only when the organiser toggle is on).
  autopostBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: 10, width: "100%", background: "#0A66C2", color: "#fff", border: "none", borderRadius: 12, padding: "12px 16px", fontSize: 15, cursor: "pointer", marginBottom: 12, fontFamily: C.body },
  sbtnIc: { width: 52, height: 52, borderRadius: 16, display: "grid", placeItems: "center", color: "#fff", fontSize: 20, fontWeight: 700, boxShadow: "0 2px 10px rgba(0,0,0,.35)" },
  sbtnL: { fontSize: 11, fontWeight: 600 },
  fallback: { marginTop: 14, padding: "12px 14px", border: `1px solid ${C.line}`, borderRadius: 12, background: C.surface2, fontSize: 12.5, color: C.ink2, display: "flex", gap: 10, alignItems: "flex-start", lineHeight: 1.45 },
  note: { fontSize: 11.5, color: C.ink3, textAlign: "center", marginTop: 13, lineHeight: 1.5 },
  msfoot: { marginTop: 20, paddingTop: 16, borderTop: `1px solid ${C.line}`, textAlign: "center" },
  impact: { display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12.5, color: C.ink2, background: C.surface2, border: `1px solid ${C.line}`, borderRadius: 999, padding: "6px 14px" },
  stickybar: { bottom: 0, zIndex: 40, margin: "16px -22px -22px", padding: "13px 22px calc(13px + env(safe-area-inset-bottom,0px))", background: "rgba(20,16,33,.94)", backdropFilter: "blur(12px)", borderTop: `1px solid ${C.line2}`, display: "flex", gap: 9, alignItems: "stretch" },
  stickyBtn: { fontFamily: C.body, fontWeight: 700, fontSize: 14.5, borderRadius: 13, padding: "15px 14px", border: "1px solid transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
  // The two universal actions the platform grid doesn't have (no duplicate of grid buttons):
  stickyCopy: { background: "linear-gradient(135deg,#6D28D9,#DB2777)", color: "#fff", flex: 1.4, boxShadow: "0 8px 22px rgba(109,40,217,.45)" },
  stickyDl: { background: C.surface2, color: C.ink, border: `1px solid ${C.line2}`, flex: 1 },
  successRing: { width: 76, height: 76, borderRadius: "50%", background: C.regSoft, color: C.reg, display: "grid", placeItems: "center", fontSize: 35, margin: "0 auto 14px" },
  miniStat: { display: "flex", gap: 10, justifyContent: "center", marginBottom: 18 },
  statBox: { background: C.surface2, border: `1px solid ${C.line}`, borderRadius: 12, padding: "11px 17px" },
  statV: { fontFamily: C.mono, fontWeight: 700, fontSize: 19, color: C.primary },
  statK: { fontSize: 11, color: C.ink3, marginTop: 2 },
  btnOutline: { fontFamily: C.body, fontWeight: 600, fontSize: 15, borderRadius: 12, padding: 14, border: `1px solid ${C.line2}`, cursor: "pointer", background: C.surface, color: C.ink, width: "100%" },
  desktopHint: { maxWidth: 452, fontSize: 12, color: C.ink3, textAlign: "center", marginTop: 16, lineHeight: 1.5 },
  toast: { position: "fixed", left: "50%", transform: "translateX(-50%)", bottom: "calc(88px + env(safe-area-inset-bottom,0px))", zIndex: 90, maxWidth: 440, width: "calc(100% - 32px)", background: "#1e1633", color: "#fff", border: "1px solid rgba(155,107,242,.5)", borderRadius: 12, padding: "12px 16px", fontSize: 13, lineHeight: 1.45, boxShadow: "0 10px 30px rgba(0,0,0,.45)", textAlign: "center" },
};

/* manual-post step modal styles */
const MS = {
  overlay: { position: "fixed", inset: 0, zIndex: 80, background: "rgba(10,7,18,.72)", backdropFilter: "blur(4px)", display: "flex", alignItems: "flex-end", justifyContent: "center", padding: 0 },
  sheet: { width: "100%", maxWidth: 452, background: C.surface, borderTopLeftRadius: 22, borderTopRightRadius: 22, border: `1px solid ${C.line2}`, borderBottom: "none", boxShadow: "0 -14px 40px rgba(0,0,0,.5)", padding: "18px 22px calc(22px + env(safe-area-inset-bottom,0px))", fontFamily: C.body, color: C.ink },
  head: { display: "flex", alignItems: "center", gap: 10, marginBottom: 10 },
  badge: { width: 34, height: 34, borderRadius: 10, display: "grid", placeItems: "center", color: "#fff", fontSize: 16, fontWeight: 700, flexShrink: 0 },
  title: { flex: 1, fontFamily: C.display, fontWeight: 600, fontSize: 17 },
  close: { background: "transparent", border: "none", color: C.ink3, fontSize: 16, cursor: "pointer", padding: 4 },
  lead: { fontSize: 13, color: C.ink2, margin: "0 0 16px", lineHeight: 1.5 },
  steps: { display: "flex", flexDirection: "column", gap: 14 },
  step: { display: "flex", gap: 12, alignItems: "flex-start" },
  num: { width: 26, height: 26, borderRadius: "50%", background: C.surface3, color: C.ink2, display: "grid", placeItems: "center", fontSize: 12.5, fontWeight: 700, fontFamily: C.mono, flexShrink: 0 },
  numDone: { background: C.reg, color: "#fff" },
  stepT: { fontSize: 14, fontWeight: 600, marginBottom: 7 },
  stepHint: { fontSize: 12, color: C.ink3, lineHeight: 1.45 },
  stepBtn: { fontFamily: C.body, fontWeight: 600, fontSize: 13.5, borderRadius: 10, padding: "9px 14px", border: `1px solid ${C.line2}`, cursor: "pointer", background: C.surface2, color: C.ink },
  stepBtnPrimary: { background: C.primarySoft, color: C.primary, border: `1px solid ${C.primary}55` },
  done: { marginTop: 18, width: "100%", fontFamily: C.body, fontWeight: 700, fontSize: 15, borderRadius: 12, padding: 14, border: "none", cursor: "pointer", background: C.primary, color: "#1a0f2e" },
};
